import { useMemo, useState } from "react";
import { Badge, Button, EmptyState, FilterChip, QueryStateBoundary } from "@/components/ui";
import { useDrinks } from "@/api/hooks/useDrinks";
import {
  DRINK_STATUSES,
  DRINK_STATUS_BADGE_VARIANT,
  DRINK_STATUS_LABELS,
  SPIRIT_BASES,
  SPIRIT_BASE_LABELS,
  isZeroProofEligible,
  type Drink,
  type DrinkStatus,
  type SpiritBase,
} from "@/api/types/drink";
import { DrinkEditorDialog } from "./DrinkEditorDialog";

type SpiritFilter = SpiritBase | "all";
type StatusFilter = DrinkStatus | "all";

/**
 * The drink library (Phase 4). This mirrors the future public website's
 * drink library: searchable card grid with spirit-base facets, status, and
 * a prominent zero-proof toggle. Clicking a card opens the same dialog used
 * for creation, in edit mode.
 */
export function DrinksPage() {
  const [spiritFilter, setSpiritFilter] = useState<SpiritFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [zeroProofOnly, setZeroProofOnly] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingDrink, setEditingDrink] = useState<Drink | null>(null);

  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useDrinks({
    // The zero-proof toggle is a client-side facet (has_na_variant OR
    // spirit_base === 'none') — no single query param covers it, so when
    // it's on we don't also narrow by the spirit-base chips server-side.
    spirit_base: !zeroProofOnly && spiritFilter !== "all" ? spiritFilter : undefined,
    status: statusFilter === "all" ? undefined : statusFilter,
    q: search,
  });

  const allDrinks = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
  const drinks = useMemo(
    () => (zeroProofOnly ? allDrinks.filter(isZeroProofEligible) : allDrinks),
    [allDrinks, zeroProofOnly],
  );

  function openCreate() {
    setEditingDrink(null);
    setFormOpen(true);
  }

  function openEdit(drink: Drink) {
    setEditingDrink(drink);
    setFormOpen(true);
  }

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-display text-2xl font-bold text-text-primary">Drink library</h1>
        <Button onClick={openCreate}>New drink</Button>
      </div>

      <div className="mb-4">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search drinks by name..."
          aria-label="Search drinks"
          className="h-10 w-full rounded-md border border-border bg-bg-card px-3 py-2 text-sm placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:max-w-md"
        />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <FilterChip label="All spirits" active={spiritFilter === "all"} onClick={() => setSpiritFilter("all")} />
        {SPIRIT_BASES.map((base) => (
          <FilterChip
            key={base}
            label={SPIRIT_BASE_LABELS[base]}
            active={spiritFilter === base}
            onClick={() => setSpiritFilter(base)}
          />
        ))}
        <span className="mx-1 w-px self-stretch bg-border" />
        <FilterChip label="All statuses" active={statusFilter === "all"} onClick={() => setStatusFilter("all")} />
        {DRINK_STATUSES.map((s) => (
          <FilterChip
            key={s}
            label={DRINK_STATUS_LABELS[s]}
            active={statusFilter === s}
            onClick={() => setStatusFilter(s)}
          />
        ))}
      </div>

      <button
        type="button"
        role="switch"
        aria-checked={zeroProofOnly}
        data-testid="zero-proof-toggle"
        onClick={() => setZeroProofOnly((v) => !v)}
        className={`mb-6 flex items-center gap-2 rounded-lg border-2 px-4 py-2 text-sm font-semibold transition-colors ${
          zeroProofOnly
            ? "border-success bg-success/10 text-success"
            : "border-border bg-bg-card text-text-secondary hover:bg-bg-hover"
        }`}
      >
        <span aria-hidden="true">{zeroProofOnly ? "✅" : "⚪"}</span>
        Zero-proof only
      </button>

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        {drinks.length === 0 ? (
          <EmptyState
            icon="🍸"
            title="No drinks yet"
            description="Add your first drink — name, story, ingredients, and an optional zero-proof twin."
            action={{ label: "New drink", onClick: openCreate }}
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {drinks.map((drink) => (
              <DrinkCard key={drink.id} drink={drink} onClick={() => openEdit(drink)} />
            ))}
          </div>
        )}
      </QueryStateBoundary>

      {hasNextPage && (
        <div className="mt-6 flex justify-center">
          <Button
            variant="secondary"
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? "Loading..." : "Load more"}
          </Button>
        </div>
      )}

      <DrinkEditorDialog open={formOpen} onClose={() => setFormOpen(false)} drink={editingDrink} />
    </div>
  );
}

interface DrinkCardProps {
  drink: Drink;
  onClick: () => void;
}

function DrinkCard({ drink, onClick }: DrinkCardProps) {
  return (
    <button
      type="button"
      data-drink-id={drink.id}
      onClick={onClick}
      className="flex flex-col overflow-hidden rounded-lg border border-border bg-bg-card text-left shadow-sm transition-colors hover:bg-bg-hover"
    >
      {drink.photo_key ? (
        <div
          className="h-32 w-full bg-bg-muted bg-cover bg-center"
          style={{ backgroundImage: `url(${drink.photo_key})` }}
        />
      ) : (
        <div className="flex h-32 w-full items-center justify-center bg-bg-muted text-3xl" aria-hidden="true">
          🍸
        </div>
      )}
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <p className="font-medium text-text-primary">{drink.name}</p>
          <Badge variant={DRINK_STATUS_BADGE_VARIANT[drink.status]} size="sm">
            {DRINK_STATUS_LABELS[drink.status]}
          </Badge>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="secondary" size="sm">
            {SPIRIT_BASE_LABELS[drink.spirit_base]}
          </Badge>
          {drink.has_na_variant && (
            <Badge variant="success" size="sm" data-testid="na-badge">
              NA ✓
            </Badge>
          )}
        </div>
        {drink.story && <p className="line-clamp-2 text-xs text-text-muted">{drink.story}</p>}
      </div>
    </button>
  );
}

